// League Table — full-season standings (Phase 3). A thin data wrapper around the
// shared <StandingsTable>: resolve the season's teams, shape them (fixture
// highlight + desktop/mobile label), and hand the rows over.
//
// Season: the module's own season dropdown (Ben, docs/QUESTIONS.md Q-003).
// app-api returns the fixture's season plus every earlier one we serve, keyed by
// label ("2026/27", "2025/26"), so switching is instant. No choice (null) = the
// current season; a season the payload lacks falls back to the current one.

import { useMemo } from "react";
import { shapeLeagueRows } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import { useBentoStore } from "../../store/index.js";
import { leagueSeasonOptions } from "../moduleControlsConfig.js";
import StandingsTable from "./StandingsTable.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface LeagueTableProps {
  fixture: string;
  season?: string | null;
}

export default function LeagueTable({ fixture, season }: LeagueTableProps) {
  const current = useBentoStore(
    (s) =>
      s.competitions.find((c) => c.competitionId === s.competitionId && c.seasonId === s.seasonId)
        ?.season
  );
  // Same rule as the dropdown: the chosen season if this league offers it.
  const competitions = useBentoStore((s) => s.competitions);
  const competitionId = useBentoStore((s) => s.competitionId);
  const offered = useMemo(() => leagueSeasonOptions(competitions, competitionId), [competitions, competitionId]);
  const chosen = season && offered.some((o) => o.value === season) ? season : current;
  const { data, isPending, isError, refetch } = useWidgetData(
    "league-table",
    useFixtureKey(fixture)
  );
  const teams =
    (chosen ? data?.[chosen] : undefined) ??
    (current ? data?.[current] : undefined) ??
    Object.values(data ?? {})[0] ??
    [];

  // Read the viewport each render (label is full-name desktop / short mobile);
  // keying the memo on it avoids a stale label, matching the legacy re-derive.
  const mobile = window.innerWidth <= 768;
  const rows = useMemo(
    () => shapeLeagueRows(teams, fixture, mobile),
    [teams, fixture, mobile]
  );

  if (isPending) return <WidgetSkeleton rows={6} />;
  if (isError) return <WidgetError onRetry={refetch} />;

  return <StandingsTable rows={rows} resetKey={`${chosen ?? ""}|${fixture}`} />;
}
